import React from "react";
import { ANGLE_SERIES, KEY_POSITIONS } from "../mockData";

const SERIES = [
  { key: "spine",    label: "Spine",    color: "#10b981" },
  { key: "hip",      label: "Hip",      color: "#4db8c8" },
  { key: "shoulder", label: "Shoulder", color: "#9b7fe8" },
  { key: "elbow",    label: "Elbow",    color: "#e8a84d" },
];

export function AngleChart({ active }: { active?: string }) {
  const w = 720, h = 240, pad = { l: 36, r: 16, t: 18, b: 28 };
  const xs = ANGLE_SERIES.map((d) => d.frame);
  const allY = ANGLE_SERIES.flatMap((d) => SERIES.map((s) => (d as any)[s.key]));
  const yMin = Math.min(...allY), yMax = Math.max(...allY);
  const yPad = (yMax - yMin) * 0.08;
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  const sx = (x: number) =>
    pad.l + ((x - xMin) / (xMax - xMin)) * (w - pad.l - pad.r);
  const sy = (y: number) =>
    pad.t + (1 - (y - (yMin - yPad)) / ((yMax + yPad) - (yMin - yPad))) * (h - pad.t - pad.b);

  const yTicks = 4;
  const yStep = (yMax + yPad - (yMin - yPad)) / yTicks;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
      {/* gridlines */}
      {Array.from({ length: yTicks + 1 }).map((_, i) => {
        const yv = yMin - yPad + i * yStep;
        const y = sy(yv);
        return (
          <g key={i}>
            <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="#eef2ef" />
            <text x={pad.l - 6} y={y + 3} fontSize="10" textAnchor="end" fill="#94a3b8">
              {Math.round(yv)}°
            </text>
          </g>
        );
      })}

      {/* phase markers */}
      {KEY_POSITIONS.map((p) => {
        const x = sx(((p.pct / 100) * (xMax - xMin)) + xMin);
        const isActive = active === p.id;
        return (
          <g key={p.id}>
            <line
              x1={x} x2={x} y1={pad.t} y2={h - pad.b}
              stroke={isActive ? "#10b981" : "#e2e8f0"}
              strokeDasharray={isActive ? "0" : "3 3"}
              strokeWidth={isActive ? 1.5 : 1}
            />
            <text
              x={x} y={h - pad.b + 14}
              fontSize="9.5" textAnchor="middle"
              fill={isActive ? "#047857" : "#94a3b8"}
              fontWeight={isActive ? 600 : 400}
            >
              {p.id}
            </text>
          </g>
        );
      })}

      {/* lines */}
      {SERIES.map((s) => {
        const d = ANGLE_SERIES
          .map((row, i) => `${i === 0 ? "M" : "L"} ${sx(row.frame)} ${sy((row as any)[s.key])}`)
          .join(" ");
        return (
          <path key={s.key} d={d} stroke={s.color} strokeWidth="1.7"
            fill="none" strokeLinecap="round" strokeLinejoin="round" />
        );
      })}

      {/* legend */}
      <g transform={`translate(${pad.l}, ${pad.t - 4})`}>
        {SERIES.map((s, i) => (
          <g key={s.key} transform={`translate(${i * 90}, 0)`}>
            <rect width="10" height="2.5" y="3" fill={s.color} rx="1" />
            <text x="14" y="8" fontSize="10.5" fill="#475569">{s.label}</text>
          </g>
        ))}
      </g>
    </svg>
  );
}
